import { motion } from 'framer-motion';

// Simple AdminCard component (inline since we don't have separate file)
function AdminCard({ name, title, delay = 0, borderColor = 'border-blue-200', particleColor = 'bg-blue-300', photoSrc }: {
  name: string;
  title: string;
  delay?: number;
  borderColor?: string;
  particleColor?: string;
  photoSrc?: string;
}) {
  return (
    <motion.div 
      className="text-center relative"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay }}
    >
      {/* PhotoWithBackdrop style effect */}
      <motion.div 
        className={`absolute -top-2 -left-2 w-full h-full border border-dashed ${borderColor} rounded-lg opacity-30`}
        animate={{ 
          rotate: [0, 1, -1, 0],
          scale: [1, 1.01, 1]
        }}
        transition={{ 
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut",
          delay: delay * 2
        }}
      />
      
      <motion.div
        className={`absolute -top-1 -right-1 w-2 h-2 rounded-full ${particleColor}`}
        animate={{ 
          y: [0, -8, 0],
          opacity: [0.5, 1, 0.5]
        }}
        transition={{ 
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut",
          delay: delay * 0.5
        }}
      />

      <div className="relative z-10 bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
        {/* Placeholder Image */}
        <div className="w-48 h-48 mx-auto mb-4 rounded-lg relative overflow-hidden">
          {photoSrc ? (
            <motion.img
              src={photoSrc}
              alt={`${name} headshot`}
              className="w-full h-full object-cover rounded-lg"
              whileHover={{ 
                scale: 1.05,
                transition: { duration: 0.3 }
              }}
            />
          ) : (
            <motion.img
              src="/images/Photo-Coming-Soon-1.png"
              alt="Photo Coming Soon"
              className="w-full h-full object-cover rounded-lg"
              whileHover={{ 
                scale: 1.05,
                transition: { duration: 0.3 }
              }}
              animate={{
                opacity: [0.8, 1, 0.8]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
          )}
        </div>

        {/* Title and Name  */}
        <h3 className="text-lg font-bold text-dblue mb-1">{title}</h3>
        <p className="text-gray-600">{name}</p>
      </div>
    </motion.div>
  );
}

export default function AdminStaff() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-amber-50 to-yellow-100">
        <div className="max-w-6xl mx-auto px-6">
          <motion.h1 
            className="text-4xl md:text-5xl font-bold text-dblue text-center mb-12"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            Admin Staff
          </motion.h1>
        </div>
      </section>

      {/* Additional Info Section */}
      <section className="py-16 bg-amber-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-dblue mb-6">
              Dedicated Administrative Support Team
            </h2>
            <p className="text-gray-600 leading-relaxed max-w-3xl mx-auto">
              Meet the dedicated administrative team supporting our centre's daily operations and family experience. 
              Our staff ensures smooth operations, excellent communication, and the highest standards of care for 
              your children and families.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Admin Staff Cards Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8">
            
            <AdminCard
              name="Ms. Racquel Morris"
              title="Director"
              delay={0.3}
              borderColor="border-blue-200"
              particleColor="bg-blue-300"
              photoSrc="/images/adminheadshots/RacquelMorris.jpg"
            />

            <AdminCard
              name="Ms. Reima Leonardo"
              title="Administrator"
              delay={0.5}
              borderColor="border-green-200"
              particleColor="bg-green-300"
              photoSrc="/images/adminheadshots/ReimaLeonardo.jpg"
            />

            <AdminCard
              name="Ms. Luchie Leonardo"
              title="Head Chef"
              delay={0.7}
              borderColor="border-purple-200"
              particleColor="bg-purple-300"
              photoSrc="/images/adminheadshots/LuchieLeonardo.jpg"
            />

          </div>
        </div>
      </section>

    </div>
  );
}


